import gql from 'graphql-tag';
import {
  condominiumAccessHoursFragment,
  condominiumAddressFragment,
  condominiumDwellerSearchUnityFragment,
  condominiumHistoryNotesFragment,
  condominiumInfoAttendanceFragment,
  condominiumPlaceAllCamerasFragment,
  condominiumPlaceCamerasFragment,
  condominiumRuleCategoryFragment,
  condominiumRuleTypeFragment,
  condominiumWorkersFragment,
  deviceFragment,
  deviceRuleFragment,
  placeFragment,
  placeInterlockableFragment,
} from './fragments';

// AccessProfile
export const accessProfiles = gql`
  query accessProfiles(
    $personContextId: Int!
    $onlyNoInvites: Boolean!
    $unityId: Int
    $profileId: Int
  ) {
    accessProfiles(
      personContextId: $personContextId
      onlyNoInvites: $onlyNoInvites
      unityId: $unityId
      profileId: $profileId
    ) {
      id
      personId
      name
      description
      isInvite
      haveVehicleDevice
      serializedParams
      isConfiguredForProfile
    }
  }
`;

export const accessProfilesByTreeNodeId = gql`
  query accessProfilesByTreeNodeId($sourceNodeId: Int!) {
    accessProfilesByTreeNodeId(sourceNodeId: $sourceNodeId) {
      id
      personId
      name
      description
    }
  }
`;

// AccessRoute
export const accessProfilePlaces = gql`
  query accessProfilePlaces($accessProfileId: Int!) {
    accessProfilePlaces(accessProfileId: $accessProfileId) {
      id
      name
      treeNodeId
      placeType {
        id
        name
      }
      haveVehicleDevice
      serializedParams
    }
  }
`;

// Modal/PutPersonAccessProfile
export const accessProfilePlaceSelect = gql`
  query accessProfilePlaceSelect($accessProfileId: Int!) {
    accessProfilePlaceSelect(accessProfileId: $accessProfileId) {
      value
      label
      isVehicular
      canBeRemoteOpened
      sharedPlace
    }
  }
`;

// DetailProvider
export const condominium = gql`
  query condominium($personContextId: Int!) {
    condominium(personContextId: $personContextId) {
      name
      email
      phone
      treeNodeId
      personId
      personContextId
      params {
        value
        name
        personContextId
      }
      unityGroupType {
        id
        label
      }
      unityType {
        id
        label
      }
      addresses {
        ...condominiumAddressParts
      }
    }
  }
  ${condominiumAddressFragment}
`;

// CondominiumAlerts
export const condominiumAlerts = gql`
  query condominiumAlerts($condominiumPersonContextId: Int!) {
    condominiumAlerts(condominiumPersonContextId: $condominiumPersonContextId) {
      title
      subtitle
      buttonLabel
      buttonRedirectUrl
      condominiumAlertType
    }
  }
`;

export const condominiumDailyAuthorizations = gql`
  query condominiumDailyAuthorizations(
    $condominiumContextId: ID!
    $filters: CondominiumDailyAuthorizationsFiltersInput
  ) {
    condominiumDailyAuthorizations(
      condominiumContextId: $condominiumContextId
      filters: $filters
    ) {
      pagination {
        page
        pageSize
        hasNextPage
      }
      collection {
        id: guestId
        inviteId
        inviteName
        guestId
        guestName
        photo
        phoneLastFourDigits: phone
        authorizedBy
        unityId
        unityName
        unityGroupName
        startDate
        endDate
        isUsed
      }
    }
  }
`;

export const condominiumDwellersSearch = gql`
  query condominiumDwellersSearch(
    $condominiumPersonContextId: Int!
    $searchText: String!
  ) {
    condominiumDwellersSearch(
      condominiumPersonContextId: $condominiumPersonContextId
      searchText: $searchText
    ) {
      ...condominiumDwellerSearchUnityParts
    }
  }
  ${condominiumDwellerSearchUnityFragment}
`;

// DeviceList
export const placeDevices = gql`
  query placeDevices($placeId: Int!, $includes: Includes) {
    placeDevices(placeId: $placeId, includes: $includes) {
      ...devicePartsForCondominiumDetail
    }
  }
  ${deviceFragment}
`;

export const condominiumPlaces = gql`
  query condominiumPlaces($condominiumId: Int!) {
    condominiumPlaces(condominiumId: $condominiumId) {
      ...placeInterlockablePartsForCondominiumDetail
    }
  }
  ${placeInterlockableFragment}
`;

export const interlockablePlaces = gql`
  query condominiumInterlockablePlaces(
    $condominiumId: Int!
    $interlockedPlaceId: Int
  ) {
    interlockablePlaces: condominiumInterlockablePlaces(
      condominiumId: $condominiumId
      interlockedPlaceId: $interlockedPlaceId
    ) {
      ...placePartsForCondominiumDetail
    }
  }
  ${placeFragment}
`;

export const interlockedPlace = gql`
  query interlockedPlace($interlockId: Int!) {
    interlockedPlace(interlockId: $interlockId) {
      id
      name
      creationDate
      places {
        place {
          id
        }
      }
    }
  }
`;

export const interlockedPlacesV2 = gql`
  query interlockedPlacesV2(
    $condominiumId: ID!
    $params: ParamsFetchInterlockPlacesInput
  ) {
    interlockedPlacesV2(condominiumId: $condominiumId, params: $params) {
      interlock {
        name
        gmt
        pause {
          pausedUntil
          by
          reason
          pausedByPersonContextId
          pausedAt
        }
        places {
          id
          name
        }
      }
    }
  }
`;

export const interlockedLinkedPlacesV2 = gql`
  query interlockedLinkedPlacesV2(
    $condominiumId: ID!
    $placeId: ID!
    $params: ParamsFetchInterlockPlacesInput
  ) {
    interlockedLinkedPlacesV2(
      condominiumId: $condominiumId
      placeId: $placeId
      params: $params
    ) {
      interlock {
        limitSelection
        places {
          id
          name
          selected
        }
      }
    }
  }
`;

export const placeAvailableDevices = gql`
  query placeAvailableDevices($placeId: Int!) {
    placeAvailableDevices(placeId: $placeId) {
      condominiumDevices {
        ...devicePartsForCondominiumDetail
      }
      partnerDevices {
        ...devicePartsForCondominiumDetail
      }
    }
  }
  ${deviceFragment}
`;

// GeneralData
export const placeTypeLabel = gql`
  query placeTypeLabels($filters: PlaceTypeLabelQueryInput!) {
    placeTypeLabels(filters: $filters) {
      unityGroupType
      unityType
    }
  }
`;

export const placeTypes = gql`
  query placesType($onlyForCondominium: Boolean) {
    placesTypeSelect(onlyForCondominium: $onlyForCondominium) {
      value
      label
    }
  }
`;

// Parameters
export const places = gql`
  query places($filters: PlaceQueryInput!) {
    places(filters: $filters) {
      collection {
        id
        name
        placeId
        placeTypeId
        comments
        serializedParams
        placeType {
          id
          name
          fieldName
        }
      }
    }
  }
`;

export const place = gql`
  query place($placeId: ID) {
    place(placeId: $placeId) {
      id
      name
      placeId
      comments
      placeTypeLabel {
        id
        label
      }
      placeTypeLabelId
      placeType {
        id
        name
        fieldName
      }
      serializedParams
      treeNodeId
    }
  }
`;

// Modal/PutDevice
export const checkSerialNumber = gql`
  query deviceCheckSerialNumber($placeId: Int!, $serialNumber: String!) {
    deviceCheckSerialNumber(placeId: $placeId, serialNumber: $serialNumber) {
      ableToUse
      reason
      device {
        id
        placeId
        deviceType {
          id
          name
          fieldName
          isCpu
          isController
        }
        serialNumber
        ip
        protocolVersion
        firmwareVersion
        name
        isOnline
      }
      cpuDevices {
        id
        name
        placeId
        deviceType {
          id
          name
        }
        serialNumber
        ip
        isOnline
      }
    }
  }
`;

// Modal/PutDevice
export const getCpuDevices = gql`
  query cpuDevices($placeId: Int!) {
    deviceCpus(placeId: $placeId) {
      id
      name
      placeId
      deviceTypeId
      serialNumber
      ip
      isOnline
    }
  }
`;
export const availableParents = gql`
  query deviceAvailableParents($deviceId: Int!, $placeId: Int!) {
    deviceAvailableParents(deviceId: $deviceId, placeId: $placeId) {
      ...devicePartsForCondominiumDetail
    }
  }
  ${deviceFragment}
`;

export const availablePlaces = gql`
  query deviceAvailablePlaces($deviceId: Int!, $placeId: Int!) {
    deviceAvailablePlaces(deviceId: $deviceId, placeId: $placeId) {
      ...placePartsForCondominiumDetail
    }
  }
  ${placeFragment}
`;

export const condominiumNotes = gql`
  query condominiumNotes($condominiumId: String) {
    condominiumNotes(condominiumId: $condominiumId) {
      id
      name
      value
    }
  }
`;

export const almahIntegrationStatus = gql`
  query almahIntegrationStatus($condominiumId: String) {
    almahIntegrationStatus(condominiumId: $condominiumId) {
      status
    }
  }
`;

export const personContextParams = gql`
  query personContextParams($personContextId: Int!, $paramName: String!) {
    personContextParams(
      personContextId: $personContextId
      paramName: $paramName
    ) {
      id
      name
      value
    }
  }
`;

export const condominiumPlaceUnities = gql`
  query condominiumPlaceUnities(
    $targetType: String
    $sourceNodeId: ID
    $filters: UnitiesFiltersInput
  ) {
    condominiumPlaceUnities(
      targetType: $targetType
      sourceNodeId: $sourceNodeId
      filters: $filters
    ) {
      collection {
        id
        name
        treeNodeId
        placeTypeId
        placeTypeLabelId
        serializedParams {
          extension
          prefixPABX
          codeAta
        }
      }
      pagination {
        totalPages
        totalResults
        page
        pageSize
      }
    }
  }
`;

export const deviceRules = gql`
  query deviceRules($deviceId: Int!) {
    deviceRules(deviceId: $deviceId) {
      ...deviceRuleParts
    }
  }
  ${deviceRuleFragment}
`;

export const listPatrolSettings = gql`
  query listPatrolSettings($condominiumId: ID) {
    listPatrolSettings(condominiumId: $condominiumId) {
      id
      condominiumId
      days
      frequency
      start {
        hours
        minutes
      }
      end {
        hours
        minutes
      }
      startTime
      endTime
      creationDate
    }
  }
`;

export const listDeviceFirmwares = gql`
  query listDeviceFirmwares(
    $condominiumId: ID
    $deviceType: String
    $chipsetType: String
  ) {
    listDeviceFirmwares(
      condominiumId: $condominiumId
      deviceType: $deviceType
      chipsetType: $chipsetType
    ) {
      id
      changelog
      version
      releaseType
      releaseDate
      releaseStatus
      allowDowngrade
      chipsetType
    }
  }
`;

export const listCurrentFirmwares = gql`
  query listCurrentFirmwares {
    listCurrentFirmwares {
      id
      version
      releaseStatus
      deviceType
      chipsetType
    }
  }
`;

export const condominiumRules = gql`
  query condominiumRules($condominiumId: Int!) {
    condominiumRules(condominiumId: $condominiumId) {
      ...condominiumRuleTypeParts
    }
  }
  ${condominiumRuleTypeFragment}
`;

export const condominiumRulesCategories = gql`
  query condominiumRulesCategories {
    condominiumRulesCategories {
      ...condominiumRuleCategoryParts
    }
  }
  ${condominiumRuleCategoryFragment}
`;

export const condominiumPlaceCameras = gql`
  query condominiumPlaceCameras($placeId: Int!, $onlyVms: Boolean) {
    condominiumPlaceCameras(placeId: $placeId, onlyVms: $onlyVms) {
      ...condominiumPlaceCamerasPart
    }
  }
  ${condominiumPlaceCamerasFragment}
`;

export const condominiumCameras = gql`
  query condominiumCameras($condominiumId: Int!) {
    condominiumCameras(condominiumId: $condominiumId) {
      cameraId
      name
      cameraType
      deviceTypeId
    }
  }
`;

export const condominiumPlaceCamerasV2 = gql`
  query condominiumPlaceCamerasV2($placeId: ID!) {
    condominiumPlaceCamerasV2(placeId: $placeId) {
      ...condominiumPlaceCamerasPart
    }
  }
  ${condominiumPlaceCamerasFragment}
`;

export const condominiumPlaceDvrCameras = gql`
  query condominiumPlaceDvrCameras($placeId: Int!) {
    condominiumPlaceDvrCameras(placeId: $placeId) {
      ...condominiumPlaceCamerasPart
    }
  }
  ${condominiumPlaceCamerasFragment}
`;

export const condominiumPlaceTowerCameras = gql`
  query condominiumPlaceTowerCameras($placeId: Int!) {
    condominiumPlaceTowerCameras(placeId: $placeId) {
      ...condominiumPlaceCamerasPart
    }
  }
  ${condominiumPlaceCamerasFragment}
`;

export const condominiumPlaceFacialCameras = gql`
  query condominiumPlaceFacialCameras($placeId: Int!) {
    condominiumPlaceFacialCameras(placeId: $placeId) {
      ...condominiumPlaceCamerasPart
    }
  }
  ${condominiumPlaceCamerasFragment}
`;

export const getCondominiumActivation = gql`
  query condominiumActivation($condominiumId: Int!) {
    condominiumActivation(condominiumId: $condominiumId) {
      name
      creationDate
    }
  }
`;

export const eventPushNotificationSetting = gql`
  query eventPushNotificationSetting(
    $condominiumContextId: String!
    $eventId: String!
  ) {
    eventPushNotificationSetting(
      condominiumContextId: $condominiumContextId
      eventId: $eventId
    ) {
      redirectToDweller
    }
  }
`;

export const eventsPushNotificationSetting = gql`
  query eventsPushNotificationSetting($condominiumContextId: String!) {
    eventsPushNotificationSetting(condominiumContextId: $condominiumContextId) {
      redirectToDweller
      eventId
    }
  }
`;

export const getCondominiumAccessHours = gql`
  query condominiumAccessHours($condominiumPersonContextId: Int!) {
    condominiumAccessHours(
      condominiumPersonContextId: $condominiumPersonContextId
    ) {
      ...condominiumAccessHoursParts
    }
  }
  ${condominiumAccessHoursFragment}
`;

export const condominiumHasGuestsForToday = gql`
  query condominiumHasGuestsForToday(
    $condominiumPersonContextId: ID!
    $unityId: ID
  ) {
    condominiumHasGuestsForToday(
      condominiumPersonContextId: $condominiumPersonContextId
      unityId: $unityId
    )
  }
`;

export const condominiumHistoryNotes = gql`
  query condominiumHistoryNotes($condominiumPersonContextId: ID!) {
    condominiumHistoryNotes(
      condominiumPersonContextId: $condominiumPersonContextId
    ) {
      ...condominiumHistoryNotesParts
    }
  }
  ${condominiumHistoryNotesFragment}
`;

export const condominiumHistoryNoteCategories = gql`
  query condominiumHistoryNoteCategories {
    condominiumHistoryNoteCategories {
      id
      name
    }
  }
`;

export const getCondominiumWorkers = gql`
  query condominiumWorkers($condominiumPersonContextId: ID!) {
    condominiumWorkers(
      condominiumPersonContextId: $condominiumPersonContextId
    ) {
      ...condominiumWorkersParts
    }
  }
  ${condominiumWorkersFragment}
`;

export const condominiumInfoAttendance = gql`
  query condominiumInfoAttendance($condominiumPersonContextId: ID!) {
    condominiumInfoAttendance(
      condominiumPersonContextId: $condominiumPersonContextId
    ) {
      ...condominiumInfoAttendanceParts
    }
  }
  ${condominiumInfoAttendanceFragment}
`;

export const condominiumPlaceAllCameras = gql`
  query condominiumPlaceAllCameras($placeId: ID!) {
    condominiumPlaceAllCameras(placeId: $placeId) {
      ...condominiumPlaceAllCamerasPart
    }
  }
  ${condominiumPlaceAllCamerasFragment}
`;

export const condominiumLocationSuggestion = gql`
  query condominiumLocationSuggestion($condominiumPersonContextId: Int!) {
    condominiumLocationSuggestion(
      condominiumPersonContextId: $condominiumPersonContextId
    ) {
      latitude
      longitude
      precise
    }
  }
`;
